import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import {
  AppliedFilterInterface,
  Button,
  Card,
  ChoiceListProps,
  ComplexAction,
  Icon,
} from '@shopify/polaris';
import { ResourcePicker, ResourcePickerProps } from '@shopify/app-bridge-react';
import { ResourcePicker as AppBridgeResourcePicker } from '@shopify/app-bridge/actions';
import { PlusMinor, SearchMinor } from '@shopify/polaris-icons';
import { useModal } from '@hooks';
import { DraggableItem, DraggableList, Separator } from '@ui';
import { CardSubTitle, CardTitleSmall, RemoveButtonText } from '@ui/common';
import Input from '@ui/input';
import styled from 'styled-components';
import { DropResult } from 'react-beautiful-dnd';
import { showHubspotChatBubbleState } from '@states';
import { useRecoilState } from 'recoil';
import ResourceModalContent, { ResourceModalContentProps } from '../content';

export interface FilterType {
  onQueryChange(queryValue: string): void;
  onQueryClear(): void;
  queryValue: string;
  queryPlaceholder?: string;
  selected?: string[];
  onChangeFilter?(selected: string[]): void;
  selectedFilterTags?: AppliedFilterInterface[];
  label?: string;
  choices?: ChoiceListProps['choices'];
  focused?: boolean;
  inputId: string;
  hideFiltersButton?: boolean;
  children?: React.ReactNode;
  disabled?: boolean;
}

export interface ModalProps {
  title: string;
  primaryAction: ComplexAction;
  secondaryAction?: ComplexAction;
  onOpen?(): void;
  onClose?(): void;
  onScrolledToBottom?(): void;
  footer?: React.ReactNode;
}

export interface DisplayedSelectedItem {
  id: string;
  item: React.ReactNode;
}

export interface ResourceModalProps extends ResourceModalContentProps {
  addButtonText?: string;
  cardTitle?: string;
  cardSubTitle?: string;
  modalProps?: ModalProps;
  displayInline?: boolean;
  displayedSelectedItems: DisplayedSelectedItem[];
  filters: FilterType;
  hideSearchWhenModalClosed?: boolean;
  showHeader?: boolean;
  onRemoveItem(id: string): void;
  useShopifyResourcePicker: boolean;
  shopifyResourceType?: ResourcePickerProps['resourceType'];
  onResourcePickerSelection?(
    selectionPayload: AppBridgeResourcePicker.SelectPayload,
  ): void;
  loading?: boolean;
  minSelectedItems?: number;
  maxSelectedItems?: number;
  draggable?: boolean;
  onDragEnd?(result: DropResult): void;
  reOrderDraggableItems?(sourceIndex: number, destinationIndex: number): void;
}

const BrowseButtonContainer = styled.div<{
  alignSelf?: 'center' | 'flex-start' | 'flex-end';
}>`
  align-self: ${(props) => props.alignSelf ?? 'center'};
  svg {
    fill: ${({ theme }) => theme.colors.font.primary.hex};
  }

  .Polaris-Icon {
    height: 1.2rem;
    width: 1.2rem;
  }
  margin-left: 0.5rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${BrowseButtonContainer} {
    flex-shrink: 0;
  }
  margin-bottom: 3.2rem;
`;

const InputContainer = styled.div`
  width: 80%;
`;

const ItemContainer = styled(HeaderContainer)`
  margin-top: 2.4rem;
`;

const MaxSelectedItemsFooter = styled.div`
  color: #6d7175;
  font-size: 12px;
`;

const ResourceModal: FunctionComponent<ResourceModalProps> = (props) => {
  const {
    addButtonText,
    cardSubTitle,
    cardTitle,
    displayInline,
    displayedSelectedItems,
    draggable,
    emptyState,
    filters,
    hideSearchWhenModalClosed,
    loading,
    maxSelectedItems,
    minSelectedItems,
    modalProps,
    onDragEnd,
    onRemoveItem,
    onResourcePickerSelection,
    reOrderDraggableItems,
    selectedItems,
    shopifyResourceType,
    useShopifyResourcePicker,
  } = props;
  const modal = useModal();
  const [modalOpen, setModalOpen] = useState(false);
  const [shopifyResourcePickerOpen, setShopifyResourcePickerOpen] =
    useState(false);
  const [selectedResourceItems, setSelectedResourceItems] = useState<
    AppBridgeResourcePicker.BaseResource[]
  >([]);
  const [, setShowHubspotChatBubble] = useRecoilState(
    showHubspotChatBubbleState,
  );

  useEffect(() => {
    const selectedResourceItems = selectedItems.map((itemId) => {
      return {
        id: `gid://shopify/${shopifyResourceType}/${itemId}`,
      };
    });
    setSelectedResourceItems(selectedResourceItems);
  }, [selectedItems, shopifyResourceType]);

  const handleModalOnClose = useCallback(() => {
    setModalOpen(false);
    filters.onQueryClear();
    modalProps?.onClose && modalProps.onClose();
  }, [filters, modalProps]);

  const onDisplayModal = (focused?: boolean) => {
    setModalOpen(true);

    const { filters } = props;

    filters.focused = focused;

    modal.display(() => <ResourceModalContent {...props} filters={filters} />, {
      ...modalProps,
      onClose: handleModalOnClose,
    });
    modalProps && modalProps.onOpen && modalProps.onOpen();
  };

  const onBrowseButtonClick = () => {
    if (useShopifyResourcePicker) {
      setShopifyResourcePickerOpen(true);
    } else {
      onDisplayModal();
    }
  };

  const handleOnInputChange = (value: string, _id: string) => {
    filters?.onQueryChange(value);

    if (!modalOpen && !useShopifyResourcePicker) {
      onDisplayModal(true);
    }

    if (!shopifyResourcePickerOpen && useShopifyResourcePicker) {
      setShopifyResourcePickerOpen(true);
    }
  };

  const handleResourcePickerOnSelection = (
    selectionPayload: AppBridgeResourcePicker.SelectPayload,
  ) => {
    if (onResourcePickerSelection) {
      setShopifyResourcePickerOpen(false);
      onResourcePickerSelection(selectionPayload);
    }
  };

  const handleResourcePickerOnCancel = () => {
    filters.onQueryClear();
    setShopifyResourcePickerOpen(false);
  };

  /* This effect is needed to prevent the props passed into the modal content from going stale */
  useEffect(() => {
    const containerProps = { ...props };
    if (modalOpen && containerProps.modalProps && !useShopifyResourcePicker) {
      let saveDisabled = false;

      if (maxSelectedItems && selectedItems.length > maxSelectedItems) {
        containerProps.modalProps.footer = (
          <MaxSelectedItemsFooter>
            You cannot select more than {maxSelectedItems} items.
          </MaxSelectedItemsFooter>
        );

        saveDisabled = true;
      }

      if (minSelectedItems && selectedItems.length < minSelectedItems) {
        containerProps.modalProps.footer = (
          <MaxSelectedItemsFooter>
            You must select at least {minSelectedItems} items.
          </MaxSelectedItemsFooter>
        );

        saveDisabled = true;
      }

      if (saveDisabled) {
        containerProps.modalProps.primaryAction = {
          content: 'Done',
          disabled: true,
          onAction: () => {
            return;
          },
        };
      }

      modal.updateContent(() => (
        <ResourceModalContent
          {...containerProps}
          emptyState={filters.queryValue === '' ? emptyState : undefined}
        />
      ));
      modal.updateProps({
        ...containerProps.modalProps,
        onClose: handleModalOnClose,
        primaryAction: {
          ...containerProps.modalProps.primaryAction,
          onAction: () => {
            handleModalOnClose();
            containerProps.modalProps?.primaryAction.onAction &&
              containerProps.modalProps?.primaryAction.onAction();
          },
        },
      });
    }
  }, [
    modalProps,
    displayedSelectedItems,
    modalOpen,
    useShopifyResourcePicker,
    selectedItems,
    minSelectedItems,
    maxSelectedItems,
    filters.queryValue,
    props,
    modal,
    handleModalOnClose,
    emptyState,
  ]);

  useEffect(() => {
    setModalOpen(false);
    setShopifyResourcePickerOpen(false);
  }, [filters.inputId]);

  useEffect(() => {
    setShowHubspotChatBubble(!shopifyResourcePickerOpen);
  }, [setShowHubspotChatBubble, shopifyResourcePickerOpen]);

  const ModalContainer = (
    <>
      <HeaderContainer>
        <div>
          <CardTitleSmall>{cardTitle}</CardTitleSmall>
          <CardSubTitle>{cardSubTitle}</CardSubTitle>
        </div>
        {!hideSearchWhenModalClosed && (
          <InputContainer>
            <Input
              placeholder={filters.queryPlaceholder}
              type="text"
              value={filters.queryValue}
              label=""
              id={filters.inputId}
              onChange={handleOnInputChange}
              prefix={<Icon source={SearchMinor}></Icon>}
              disabled={loading}
            />
          </InputContainer>
        )}

        <BrowseButtonContainer
          alignSelf={hideSearchWhenModalClosed ? 'center' : 'flex-end'}
        >
          {!loading && (
            <Button
              primary
              icon={PlusMinor}
              onClick={() => onBrowseButtonClick()}
            >
              {addButtonText ? addButtonText : 'Browse'}
            </Button>
          )}
          {loading && <Button disabled>Syncing</Button>}
        </BrowseButtonContainer>
      </HeaderContainer>

      {!draggable &&
        displayedSelectedItems.map((selectedItem, index) => (
          <div key={selectedItem.id}>
            <ItemContainer>
              {selectedItem.item}
              <RemoveButtonText onClick={() => onRemoveItem(selectedItem.id)}>
                Remove
              </RemoveButtonText>
            </ItemContainer>
            {displayedSelectedItems.length !== index + 1 && <Separator />}
          </div>
        ))}
      {draggable && onDragEnd && reOrderDraggableItems && (
        <ItemContainer>
          <DraggableList
            onDragEnd={onDragEnd}
            droppableId="selectedCharitiesTag"
          >
            {displayedSelectedItems.map((selectedItem, index) => (
              <DraggableItem
                onRemove={onRemoveItem}
                reOrderDraggableItems={reOrderDraggableItems}
                id={selectedItem.id}
                key={selectedItem.id}
                index={index}
                totalChildren={displayedSelectedItems.length}
              >
                {selectedItem.item}
              </DraggableItem>
            ))}
          </DraggableList>
        </ItemContainer>
      )}
      {useShopifyResourcePicker && shopifyResourceType && (
        <ResourcePicker
          resourceType={shopifyResourceType}
          onSelection={handleResourcePickerOnSelection}
          open={shopifyResourcePickerOpen}
          initialQuery={filters.queryValue}
          onCancel={handleResourcePickerOnCancel}
          selectMultiple={true}
          initialSelectionIds={selectedResourceItems}
        />
      )}
    </>
  );

  if (!displayInline) {
    return <Card sectioned>{ModalContainer}</Card>;
  }

  return ModalContainer;
};

export default ResourceModal;
